import API_BASE from "../../utils/api";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

const emptyForm = () => ({
  label: "",
  fieldType: "text",
  options: "",
  required: false,
  appliesTo: ["student"],
  order: 0,
});

const CustomFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });

  const fetchFields = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/custom-fields`);
      setFields(res.data);
    } catch (_) {
      setStatus({ type: "error", message: "Failed to load custom fields" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  useEffect(() => {
    if (status.message) {
      const t = setTimeout(() => setStatus({ type: "", message: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [status]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setError("");
    setShowModal(true);
  };

  const openEdit = (field) => {
    setEditing(field);
    setForm({
      label: field.label,
      fieldType: field.fieldType,
      options: (field.options || []).join(", "),
      required: field.required,
      appliesTo: field.appliesTo,
      order: field.order || 0,
    });
    setError("");
    setShowModal(true);
  };

  const toggleAppliesTo = (value) => {
    setForm((f) => ({
      ...f,
      appliesTo: f.appliesTo.includes(value) ? f.appliesTo.filter((v) => v !== value) : [...f.appliesTo, value],
    }));
  };

  const handleSave = async () => {
    setError("");
    if (!form.label.trim()) {
      setError("Label is required");
      return;
    }
    const payload = {
      label: form.label,
      fieldType: form.fieldType,
      options: form.fieldType === "dropdown" ? form.options.split(",").map((o) => o.trim()).filter(Boolean) : [],
      required: form.required,
      appliesTo: form.appliesTo.length ? form.appliesTo : ["student"],
      order: Number(form.order) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API_BASE}/custom-fields/${editing._id}`, payload);
        setStatus({ type: "success", message: "Field updated" });
      } else {
        await axios.post(`${API_BASE}/custom-fields`, payload);
        setStatus({ type: "success", message: "Field created" });
      }
      setShowModal(false);
      fetchFields();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save field");
    }
  };

  const toggleActive = async (field) => {
    try {
      await axios.put(`${API_BASE}/custom-fields/${field._id}`, { isActive: !field.isActive });
      fetchFields();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to update field" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE}/custom-fields/${deleteTarget._id}`);
      setStatus({ type: "success", message: "Field deleted" });
      setDeleteTarget(null);
      fetchFields();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to delete field" });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Custom Fields</h1>
            <ProfileDropdown />
          </div>
        </nav>

        {status.message && (
          <div
            className={`fixed top-4 right-4 z-[100] max-w-sm p-4 rounded-lg shadow-lg ${
              status.type === "success" ? "bg-green-100 text-green-700 border border-green-400" : "bg-red-100 text-red-700 border border-red-400"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="p-6">
          <div className="bg-white rounded-lg shadow p-6 mb-6 flex justify-between items-center flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold">School-Defined Fields</h2>
              <p className="text-sm text-gray-500">
                Add fields your school needs on student records (and optionally the admission form) beyond the built-in ones.
              </p>
            </div>
            <button onClick={openAdd} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition flex items-center">
              <span className="mr-2">+</span> Add Field
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Label</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Applies To</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Required</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading…</td></tr>
                  ) : fields.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="text-4xl mb-2">🧩</div>
                      <p>No custom fields yet</p>
                      <button onClick={openAdd} className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm">Add your first field</button>
                    </td></tr>
                  ) : (
                    fields.map((f) => (
                      <tr key={f._id} className="hover:bg-gray-50">
                        <td className="px-5 py-4 font-medium text-gray-800">{f.label}</td>
                        <td className="px-5 py-4 text-sm text-gray-600 capitalize">{f.fieldType}</td>
                        <td className="px-5 py-4 text-sm text-gray-600 capitalize">{f.appliesTo.join(", ")}</td>
                        <td className="px-5 py-4 text-sm">{f.required ? "Yes" : "No"}</td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => toggleActive(f)}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                          >
                            {f.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button onClick={() => openEdit(f)} className="text-amber-600 hover:underline text-sm mr-3">Edit</button>
                          <button onClick={() => setDeleteTarget(f)} className="text-red-600 hover:underline text-sm">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editing ? "Edit Field" : "Add Field"}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Save</button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
              <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="w-full border rounded-md px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
              <select value={form.fieldType} onChange={(e) => setForm({ ...form, fieldType: e.target.value })} className="w-full border rounded-md px-3 py-2">
                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {form.fieldType === "dropdown" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Options (comma-separated)</label>
                <input type="text" value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="Yes, No, N/A" className="w-full border rounded-md px-3 py-2" />
              </div>
            )}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} /> Required
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.appliesTo.includes("student")} onChange={() => toggleAppliesTo("student")} /> Student Profile
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.appliesTo.includes("admission")} onChange={() => toggleAppliesTo("admission")} /> Admission Form
                </label>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Field"
          message={`Delete "${deleteTarget.label}"? Existing values saved on students will remain but will no longer be shown or editable.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default CustomFields;
